// hooks/useExportTable.js
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const useExportTable = (tableId) => {
  const cleanTable = (excluded = []) => {
    const table = document.getElementById(tableId);
    if (!table) {
      console.error(`Table with id "${tableId}" not found.`);
      return null;
    }

    // Clone table to avoid modifying DOM
    const clone = table.cloneNode(true);

    // Convert excluded to lowercase if it's header names
    const excludedLower = excluded.map((e) =>
      typeof e === "string" ? e.toLowerCase() : e
    );

    // Identify column indexes to remove (by header names or indexes)
    const headerRow = clone.querySelector("thead tr");
    let indexesToRemove = [];

    if (headerRow) {
      const headers = Array.from(headerRow.children).map((th) =>
        th.innerText.trim().toLowerCase()
      );
      excludedLower.forEach((ex) => {
        if (typeof ex === "number") indexesToRemove.push(ex);
        else {
          const i = headers.indexOf(ex);
          if (i !== -1) indexesToRemove.push(i);
        }
      });
    }

    // Remove cells from every row
    Array.from(clone.rows).forEach((row) => {
      indexesToRemove
        .sort((a, b) => b - a) // remove right-to-left to keep indexes correct
        .forEach((i) => {
          if (row.cells[i]) row.deleteCell(i);
        });
    });

    return clone;
  };

  const exportTable = (type = "xlsx", filename = "table_data", excludedCols = []) => {
    const table = cleanTable(excludedCols);
    if (!table) return;

    const worksheet = XLSX.utils.table_to_sheet(table);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    let blob;
    let finalFilename = filename;

    if (type === "csv") {
      const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
      blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
      finalFilename += ".csv";
    } else if (type === "xlsm") {
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsm", type: "array" });
      blob = new Blob([excelBuffer], {
        type: "application/vnd.ms-excel.sheet.macroEnabled.12",
      });
      finalFilename += ".xlsm";
    } else {
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      finalFilename += ".xlsx";
    }

    saveAs(blob, finalFilename);
  };

  return {
    exportToCSV: (filename, excludedCols) => exportTable("csv", filename, excludedCols),
    exportToExcel: (filename, excludedCols) => exportTable("xlsx", filename, excludedCols),
    exportToExcelMacro: (filename, excludedCols) =>
      exportTable("xlsm", filename, excludedCols),
  };
};

export default useExportTable;
